/**
 * Pravni dokumenti platforme - objavljivanje novih verzija.
 *
 * Tekst je **jedan za sve klijente**, pa je ovo platformska strana: prodavac ga
 * ne vidi, podrška ga čita ali ne menja (`sysadmin-rbac.ts`).
 *
 * Objavljivanje nove verzije samo po sebi traži novo prihvatanje od svih
 * korisnika - gejt poredi broj verzije. Zato ovde nema dugmeta „traži ponovno
 * prihvatanje": ono postoji po klijentu, na kartici Klijent → Uslovi, za slučaj
 * kad se tekst nije menjao a prihvatanje ipak treba ponoviti.
 *
 * Stara verzija se ne briše - prihvatanja koja na nju pokazuju moraju da imaju
 * šta da pokažu (ADR-042).
 */
import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, FileText, RefreshCw, Save } from 'lucide-react';
import { api } from '../../api/client';

type Kind = 'terms' | 'privacy' | 'contract' | 'affiliate_terms';

type VersionRow = {
  id: string;
  kind: Kind;
  version: number;
  title: string;
  changeSummary: string;
  requiresAcceptance: boolean;
  isCurrent: boolean;
  effectiveFrom: string;
};

type FullDocument = VersionRow & { body: string };

const NAZIV: Record<Kind, string> = {
  terms: 'Uslovi korišćenja',
  privacy: 'Politika privatnosti',
  contract: 'Šablon ugovora',
  affiliate_terms: 'Uslovi partnerskog programa',
};

type Polje = { field: string; opis: string };

function datum(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '-' : d.toLocaleDateString('sr-RS');
}

export function LegalDocumentsPage() {
  const [kind, setKind] = useState<Kind>('terms');
  const [verzije, setVerzije] = useState<VersionRow[]>([]);
  const [tekuci, setTekuci] = useState<FullDocument | null>(null);
  const [naslov, setNaslov] = useState('');
  const [telo, setTelo] = useState('');
  const [sazetak, setSazetak] = useState('');
  const [trazi, setTrazi] = useState(true);
  const [ucitavanje, setUcitavanje] = useState(false);
  const [greska, setGreska] = useState<string | null>(null);
  const [poruka, setPoruka] = useState<string | null>(null);
  const [salje, setSalje] = useState(false);
  const [polja, setPolja] = useState<Polje[]>([]);

  // Spisak polja se cita jednom - isti je za sve dokumente, a menja se samo sa
  // novom verzijom servera.
  useEffect(() => {
    api.get<{ fields: Polje[] }>('/admin/legal/contract-fields')
      .then((r) => setPolja(r.fields ?? []))
      .catch(() => { /* pomoc pri pisanju nije uslov da se sablon objavi */ });
  }, []);

  const refresh = useCallback(async () => {
    setUcitavanje(true);
    setGreska(null);
    try {
      const [spisak, current] = await Promise.all([
        api.get<{ documents: VersionRow[] }>(`/admin/legal/documents?kind=${kind}`),
        api.get<{ documents: FullDocument[] }>('/admin/legal/documents/current'),
      ]);
      setVerzije(spisak.documents);
      const tekuca = current.documents.find((d) => d.kind === kind) ?? null;
      setTekuci(tekuca);
      // Nova verzija se piše **od tekuće**: pravni tekst se dopunjuje, ne piše
      // iz početka, a prazno polje bi na to teralo.
      setNaslov(tekuca?.title ?? NAZIV[kind]);
      setTelo(tekuca?.body ?? '');
      setSazetak('');
      setTrazi(tekuca?.requiresAcceptance ?? true);
    } catch (e: any) {
      setGreska(e?.message || 'Greška pri učitavanju');
    } finally {
      setUcitavanje(false);
    }
  }, [kind]);

  useEffect(() => { void refresh(); }, [refresh]);

  const izmenjen = !!tekuci && (telo.trim() !== tekuci.body.trim() || naslov.trim() !== tekuci.title.trim());

  const objavi = async () => {
    if (!naslov.trim() || !telo.trim() || salje) return;
    setSalje(true);
    setGreska(null);
    setPoruka(null);
    try {
      const doc = await api.post<FullDocument>('/admin/legal/documents', {
        kind,
        title: naslov.trim(),
        body: telo,
        changeSummary: sazetak.trim(),
        requiresAcceptance: kind === 'affiliate_terms' ? false : trazi,
      });
      setPoruka(
        `Objavljena verzija ${doc.version}.` +
        (kind === 'affiliate_terms' ? ' Prihvataju je firme koje od sada traže pristup partnerskim ponudama.'
          : trazi ? ' Svi korisnici će je prihvatiti pri sledećoj prijavi.' : ' Dokument se samo čita, prijava se zbog njega ne zaustavlja.'),
      );
      await refresh();
    } catch (e: any) {
      setGreska(e?.data?.message || e?.message || 'Objavljivanje nije uspelo');
    } finally {
      setSalje(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <header style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Pravni dokumenti
        </h2>
        <button onClick={() => void refresh()} style={btnGhost}><RefreshCw size={13} /> Osveži</button>
      </header>

      <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)', maxWidth: 720 }}>
        Tekst važi za sve klijente. Objavljivanje nove verzije traži novo prihvatanje od svih
        korisnika - stara verzija ostaje zabeležena, kao i sva prihvatanja data na nju.
      </p>

      <div style={{ display: 'flex', gap: 8 }}>
        {(['terms', 'privacy', 'contract', 'affiliate_terms'] as Kind[]).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
              border: `1px solid ${kind === k ? 'var(--color-primary)' : 'var(--color-border)'}`,
              background: kind === k ? 'rgba(37,99,235,0.08)' : 'transparent',
              color: kind === k ? 'var(--color-primary)' : 'var(--color-text-muted)',
              fontWeight: kind === k ? 600 : 500,
            }}
          >
            <FileText size={14} /> {NAZIV[k]}
          </button>
        ))}
      </div>

      {greska && <div style={upozorenje}><AlertTriangle size={14} /> {greska}</div>}
      {poruka && <div style={obavestenje}>{poruka}</div>}
      {ucitavanje && <p style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Učitavanje…</p>}

      <section style={kartica}>
        <h3 style={{ margin: '0 0 4px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Nova verzija
        </h3>
        <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--color-text-muted)' }}>
          {tekuci
            ? `Polazi od verzije ${tekuci.version} (važi od ${datum(tekuci.effectiveFrom)}). Broj verzije dodeljuje server.`
            : 'Još nema objavljene verzije ovog dokumenta.'}
        </p>

        <label style={oznaka}>Naslov</label>
        <input value={naslov} onChange={(e) => setNaslov(e.target.value)} style={polje} />

        <label style={oznaka}>Šta se promenilo (korisnik ovo vidi iznad teksta)</label>
        <input
          value={sazetak}
          onChange={(e) => setSazetak(e.target.value)}
          placeholder="npr. dopunjen član o naplati i rokovima čuvanja"
          style={polje}
        />

        <label style={oznaka}>Tekst (Markdown: ## naslov, - lista, 1. lista, tabela, **podebljano**)</label>
        <textarea
          value={telo}
          onChange={(e) => setTelo(e.target.value)}
          rows={22}
          style={{ ...polje, fontFamily: 'ui-monospace, Menlo, Consolas, monospace', lineHeight: 1.5, resize: 'vertical' }}
        />

        {kind === 'contract' && polja.length > 0 && (
          <details style={{ marginTop: 10 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12, fontWeight: 600, color: 'var(--color-text-strong)' }}>
              Polja koja se popunjavaju ({polja.length})
            </summary>
            <p style={{ margin: '8px 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
              Piše se kao <code>{'{{klijent.pib}}'}</code>. Polje koje server ne ume da popuni
              <strong> ostaje vidljivo u tekstu</strong> i prijavljuje se pri izdavanju - rupa u ugovoru
              ne sme da prođe neopaženo.
            </p>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                {polja.map((f) => (
                  <tr key={f.field}>
                    <td style={{ ...td, width: 180, fontFamily: 'ui-monospace, Menlo, Consolas, monospace' }}>
                      {`{{${f.field}}}`}
                    </td>
                    <td style={td}>{f.opis}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

        {kind === 'contract' ? (
          /* Sablon ima nepopunjena polja - kad bi stajao u gejtu prijave,
             korisnik bi pristajao na tekst sa viticama umesto svog PIB-a. Zato
             ovde nema izbora: server to i iznudjuje, ne samo ekran. */
          <p style={{ margin: '12px 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
            Šablon ugovora <strong>ne ulazi u prijavu</strong>. Iz njega se ugovor izdaje pojedinačnom
            klijentu na kartici <strong>Klijent → Ugovori</strong>, i tek tada se popunjavaju podaci.
          </p>
        ) : kind === 'affiliate_terms' ? (
          /* Server ovoj vrsti uvek upisuje requiresAcceptance=false: uslove
             programa prihvata samo firma koja trazi pristup, ne svi pri prijavi. */
          <p style={{ margin: '12px 0', fontSize: 12, color: 'var(--color-text-muted)' }}>
            Uslovi partnerskog programa <strong>ne ulaze u prijavu</strong>. Prihvata ih administrator firme kad
            zatraži pristup partnerskim ponudama.
          </p>
        ) : (
          <>
            <label style={{ ...oznaka, display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500 }}>
              <input type="checkbox" checked={trazi} onChange={(e) => setTrazi(e.target.checked)} />
              Prijava staje dok se ovo ne prihvati
            </label>
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--color-text-muted)' }}>
              Isključeno = dokument se samo čita. Koristi se za obaveštenja koja ne menjaju obavezu.
            </p>
          </>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button onClick={() => void objavi()} disabled={salje || !naslov.trim() || !telo.trim()} style={btnPrimary}>
            <Save size={14} /> {salje ? 'Objavljujem…' : 'Objavi novu verziju'}
          </button>
          {!izmenjen && tekuci && (
            <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
              Tekst je isti kao u tekućoj verziji - objavljivanjem bi svi korisnici prihvatali
              nepromenjen dokument.
            </span>
          )}
        </div>
      </section>

      <section style={kartica}>
        <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 600, color: 'var(--color-text-strong)' }}>
          Objavljene verzije
        </h3>
        {verzije.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, color: 'var(--color-text-muted)' }}>Nema nijedne verzije.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Verzija', 'Važi od', 'Šta se promenilo', 'Prihvatanje', ''].map((h) => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {verzije.map((v) => (
                <tr key={v.id}>
                  <td style={td}><strong>v{v.version}</strong></td>
                  <td style={td}>{datum(v.effectiveFrom)}</td>
                  <td style={td}>{v.changeSummary || '-'}</td>
                  <td style={td}>{v.requiresAcceptance ? 'obavezno' : 'samo čitanje'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    {v.isCurrent && (
                      <span style={{ padding: '3px 8px', borderRadius: 999, fontSize: 12, background: 'rgba(22,163,74,0.12)', color: '#15803d' }}>
                        važeća
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

const kartica: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 10, padding: 20,
  background: 'var(--color-bg-surface, #fff)',
};

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: '0.04em', color: 'var(--color-text-muted)',
  borderBottom: '1px solid var(--color-border)',
};

const td: React.CSSProperties = { padding: '10px', borderBottom: '1px solid var(--color-border)' };

const oznaka: React.CSSProperties = {
  display: 'block', fontSize: 12, fontWeight: 600, margin: '12px 0 4px',
  color: 'var(--color-text-strong)',
};

const polje: React.CSSProperties = {
  width: '100%', padding: '8px 10px', borderRadius: 6, fontSize: 13,
  border: '1px solid var(--color-border)', background: 'var(--color-bg-surface, #fff)',
  color: 'var(--color-text-strong)', boxSizing: 'border-box',
};

const btnPrimary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 14px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
  border: 'none', background: 'var(--color-primary, #2563eb)', color: '#fff', fontWeight: 600,
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 5,
  padding: '5px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
  border: '1px solid var(--color-border)', background: 'transparent',
  color: 'var(--color-text-muted)',
};

const upozorenje: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 12px', borderRadius: 6,
  background: 'rgba(220,38,38,0.08)', color: '#dc2626', fontSize: 13,
};

const obavestenje: React.CSSProperties = {
  padding: '8px 12px', borderRadius: 6,
  background: 'rgba(22,163,74,0.10)', color: '#15803d', fontSize: 13,
};
